import BaseModel from '@core/model';

import CustodyAccountStatusEnum from '@constants/enums/custodyAccount/status';

import AccountApi from '@packages/accounts/api';

import { wait } from '@utils/async';
import { toCurrency } from '@utils/number';

import i18n from '@plugins/i18n';

import { Custodian, Account, Adviser, Investor } from '@/models';

export class CustodyAccount extends BaseModel {
    static name = 'CustodyAccount';
    static entity = 'custodyAccount';
    static cacheOriginal = true;

    static fields() {
        return {
            ...super.fields(),
            id: this.string(null).nullable(),
            reference: this.string(null).nullable(),
            joint_account: this.boolean(null).nullable(),
            status: this.enum(CustodyAccountStatusEnum).nullable(),
            balance: this.attr(null).nullable(),

            custodian_id: this.string(null).nullable(),
            custodian: this.belongsTo(Custodian, 'custodian_id'),

            account_id: this.string(null).nullable(),
            account: this.belongsTo(Account, 'account_id'),

            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id')
        };
    }

    get is_valid() {
        return !!this.reference && (this.custodian_id || (this.custodian && this.custodian.name));
    }

    get computed_name() {
        if (this.is_valid) {
            return [this.custodian.name, this.reference].join(' - ');
        }
        return i18n.t('custody_account');
    }

    get pretty_balance() {
        const amount = this.balance && this.balance.amount !== null ? this.balance.amount : 0;
        const currency = (this.balance && this.balance.currency) || 'GBP';

        return toCurrency(amount, currency);
    }

    get has_positive_balance() {
        return this.balance && this.balance.amount > 0;
    }

    get is_active() {
        return this.status === CustodyAccountStatusEnum.ACTIVE;
    }

    get is_inactive() {
        return this.status === CustodyAccountStatusEnum.INACTIVE;
    }

    get is_pending() {
        return this.status === CustodyAccountStatusEnum.PENDING;
    }

    get is_for_unlisted() {
        if (
            this.custodian &&
            (this.custodian.default_unlisted || this.custodian.name === 'Woodside Corporate Services Limited')
        ) {
            return true;
        }

        return false;
    }

    get is_for_listed() {
        if (
            this.custodian &&
            (this.custodian.default_listed || this.custodian.name === 'James Brearley & Sons Limited')
        ) {
            return true;
        }

        return false;
    }

    get is_for_isa() {
        if (!this.account) {
            return false;
        }

        return this.account.is_type_isa || this.account.is_type_isa_fund;
    }

    is_for_platform() {
        if (!this.account) {
            return false;
        }

        return this.account.is_type_platform;
    }

    is_for_fee() {
        if (!this.account) {
            return false;
        }

        return this.account.is_type_fee;
    }

    static async $create(data = null) {
        if (!data) {
            throw new Error('Missing data when creating custody account');
        }

        if (!data.account_id) {
            throw new Error('Missing data.account_id when creating custody account');
        }

        return await new AccountApi().methods.postCustodyAccount(data.account_id, data);
    }

    async $toggleStatus() {
        this.$saving = ['status'];

        await wait(600);

        let status = this.status;

        if (this.is_active) {
            status = CustodyAccountStatusEnum.INACTIVE;
        } else if (this.is_inactive) {
            status = CustodyAccountStatusEnum.PENDING;
        } else if (this.is_pending) {
            status = CustodyAccountStatusEnum.ACTIVE;
        }

        let response = null;

        try {
            response = await new AccountApi().methods.patchCustodyAccount(this.account_id, this.id, { status });
        } finally {
            this.$saving = this.$saving.filter(saving => saving !== 'status');
        }

        return response;
    }

    async $updateReference(reference) {
        this.$saving = ['reference'];

        await wait(300);

        let response = null;

        try {
            response = await new AccountApi().methods.patchCustodyAccount(this.account_id, this.id, { reference });
        } finally {
            this.$saving = this.$saving.filter(saving => saving !== 'reference');
        }

        return response;
    }

    async $remove() {
        this.$removing = true;

        await wait(300);

        let response = null;

        try {
            response = await new AccountApi().methods.removeCustodyAccount(this.account_id, this.id);
        } finally {
            this.$removing = false;
        }

        return response;
    }
}

export default CustodyAccount;
